import type {
  ContentPart,
  ModelInfo,
  ProviderCapabilities,
  ProviderPreset,
  ProviderType,
  TestResult,
} from "@qn/providers";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    /** Server-side request id: quote it when reporting a problem. */
    public requestId?: string,
  ) {
    super(message);
  }
}

export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const method = init.method ?? "GET";
  const once = () =>
    fetch(`/api${path}`, {
      method,
      credentials: "same-origin",
      signal: init.signal,
      headers: {
        "x-qn-client": "web",
        ...(init.body !== undefined ? { "content-type": "application/json" } : {}),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  // Reads are safe to repeat: ride out a dropped connection or a brief 502/503/504 (2 retries, short backoff).
  let res: Response | undefined;
  for (let attempt = 0; ; attempt++) {
    try {
      res = await once();
      if (method !== "GET" || ![502, 503, 504].includes(res.status) || attempt >= 2) break;
    } catch (e) {
      if (method !== "GET" || init.signal?.aborted || attempt >= 2) throw e;
    }
    await new Promise((r) => setTimeout(r, 300 * 2 ** attempt));
  }
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as T;
}

export async function toApiError(res: Response): Promise<ApiError> {
  let message = `Request failed (${res.status})`;
  let code: string | undefined;
  let requestId = res.headers.get("x-request-id") ?? undefined;
  try {
    const j = (await res.json()) as {
      error?: { message?: string; code?: string; requestId?: string };
    };
    message = j.error?.message ?? message;
    code = j.error?.code;
    requestId = j.error?.requestId ?? requestId;
  } catch {
    /* non-JSON body */
  }
  return new ApiError(res.status, message, code, requestId);
}

// ---- Shared shapes (mirror apps/api responses) ----
export interface User {
  id: string;
  email: string;
  name: string;
  role: "owner" | "user";
}
export interface MeResponse {
  user: User | null;
  needsSetup: boolean;
  signupOpen: boolean;
  mustSetup2fa?: boolean;
  /** Instance feature switches (false = switched off by the owner). */
  features?: Record<string, boolean>;
  /** Owner-chosen look defaults, used before sign-in. */
  defaults?: { theme?: string; style?: string; accent?: string; locale?: string };
}
export interface ModelRef {
  providerId: string;
  model: string;
}
export interface UserSettings {
  theme?: import("@qn/ui").ThemeMode;
  style?: "modern" | "classic" | "glass";
  accent?: string;
  locale?: "en" | "ar";
  density?: "comfortable" | "compact";
  fontScale?: "sm" | "md" | "lg" | "xl";
  customInstructions?: string;
  sendOnEnter?: boolean;
  chatWidth?: "narrow" | "normal" | "wide";
  readingFont?: "sans" | "serif" | "mono";
  reduceMotion?: boolean;
  lockZoom?: boolean;
  imageSafeMode?: boolean;
  autoSearch?: boolean;
  autoSkills?: boolean;
  imageProviderId?: string;
  imageModel?: string;
  pushEnabled?: boolean;
  notifyRunDone?: boolean;
  notifyApproval?: boolean;
  notifyFailures?: boolean;
  showUsage?: boolean;
  defaultModel?: ModelRef | null;
  assistantModel?: ModelRef | null;
  fallbackChain?: ModelRef[];
  waterfall?: boolean;
  replyLanguage?: string;
  replyTone?: string;
  replyEmoji?: boolean;
  suggestStarters?: boolean;
  suggestAutocomplete?: boolean;
  suggestAi?: boolean;
  suggestHistory?: boolean;
  waterfallMode?: "auto" | "manual";
  waterfallFreeOnly?: boolean;
  workersAiGuard?: { enabled: boolean; thresholdPct: number; dailyNeurons: number };
  toolPermissions?: Record<string, "ask" | "allow" | "deny">;
  searchProvider?: "tavily" | "brave" | "serper";
  memoryEnabled?: boolean;
  memoryAuto?: boolean;
  usageLimits?: { monthlyUsd: number | null };
  voice?: import("./voice").VoiceSettings;
}
export interface PublicProvider {
  id: string;
  name: string;
  type: ProviderType;
  baseUrl: string;
  hasKey: boolean;
  needsKey?: boolean;
  headers: Record<string, string>;
  accountId: string | null;
  models: ModelInfo[];
  billing?: { level: "yes" | "maybe"; reason: "paid" | "credits" | "card" | "upgrade" | "workers" };
  capabilities: ProviderCapabilities;
  instructions?: string;
  enabled: boolean;
}
export interface PickerModel extends ModelRef {
  providerName: string;
  providerType: ProviderType;
  label: string;
  capabilities: ProviderCapabilities;
  priceInPerM: number | null;
  priceOutPerM: number | null;
  cost: "free" | "quota" | "paid";
  strength: number;
  /** 1 (slow) – 5 (very fast). */
  speed?: number;
}
export interface Conversation {
  id: string;
  title: string;
  providerId: string | null;
  model: string | null;
  pinned: boolean;
  archived: boolean;
  mode: "chat" | "agent";
  agentId: string | null;
  projectId: string | null;
  planMode: boolean;
  runStatus: string;
  pending: PendingCall[] | null;
  tasks: Array<{ id: string; content: string; status: "pending" | "in_progress" | "completed" }>;
  permissions: Record<string, "ask" | "allow" | "deny">;
  createdAt: number;
  updatedAt: number;
}

export interface PendingCall {
  id: string;
  name: string;
  input: Record<string, unknown>;
  decision: "approved" | "denied" | "pending";
  reason?: string;
}
export interface Message {
  id: string;
  role: "user" | "assistant";
  content: ContentPart[];
  providerId: string | null;
  providerName: string | null;
  model: string | null;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  status: "complete" | "error" | "aborted";
  error: string | null;
  createdAt: number;
}
export type { ProviderPreset, TestResult };

export const textOf = (parts: ContentPart[], type: "text" | "thinking" = "text") =>
  parts.map((p) => (p.type === type ? p.text : "")).join("");
